import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import moment from 'moment-timezone';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';

@Component({
  standalone: false,
  selector: 'invd-collateral-analyzer-error-container',
  templateUrl: './collateral-analyzer-error-container.component.html',
  styleUrls: ['./collateral-analyzer-error-container.component.scss'],
})
export class CollateralAnalyzerErrorContainerComponent implements OnInit {
  @Input() genericErrorMesasgesList: any;
  @Input() collateralValidatedPoolListRes: any;
  @Input() validationErrorList: any;
  @Input() compositeErrorList: any;
  @Input() fileName: any;
  @Input() hasValidationErrors: boolean;

  private fmLoadingPageService = inject(FmLoadingPageService);

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  public downloadPDFErrorReport(): void {
    const downloadedTime = moment(new Date());

    const downloadErrorReportName =
      'Collateral_Analyzer_Error_Report_' + downloadedTime.format('YYYYMMDDHHmmss') + '.pdf';

    const errorCount = this.validationErrorList && this.validationErrorList.length;
    pdfMake.addVirtualFileSystem(pdfFonts);

    const dd = {
      content: [
        { text: 'Fannie Mae Structured Transactions', style: 'header' },
        { text: 'Collateral Analyzer upload - Error Report', style: 'subheaderText' },
        {
          text: 'Upload date: ' + this.collateralValidatedPoolListRes.uploadDate,
          style: 'uploadedText',
        },
        { text: 'Uploaded file: ' + this.fileName, style: 'uploadedText' },
        {
          text: 'Uploaded by: ' + this.collateralValidatedPoolListRes.uploadedBy,
          style: 'uploadedText',
        },
        { text: this.compositeErrorHeader(), style: 'subheader' },
        {
          style: 'tableExample',
          table: {
            headerRows: 1,
            body: this.compositeErrorTableData(),
          },
          layout: {
            defaultBorder: false,
          },
        },
        {
          style: 'tableExample',
          table: {
            headerRows: 1,
            widths: ['50%', '50%'],
            body: [
              [
                { text: 'Pool-level Errors', style: 'subheader', alignment: 'left' },
                {
                  text: errorCount + ' Total',
                  style: 'subheader',
                  alignment: 'right',
                },
              ],
            ],
          },
          layout: 'noBorders',
        },
        {
          style: 'tableExampleValidationError',
          table: {
            headerRows: 1,
            widths: ['16%', '6%', '80%', '2%'],
            body: this.validationErrorTableData(),
          },
          layout: {
            defaultBorder: false,
          },
        },
        {
          text: [
            '* For assistance with this error, please contact Structured Transaction support at ',
            { text: 'sto_megas@fanniemae.com ', style: 'textEmail' },
            ' or call 800-555-1234.',
          ],
          style: 'bottomText',
        },
      ],
      styles: {
        header: {
          fontSize: 16,
          bold: true,
          margin: [0, 0, 0, 0],
        },
        subheaderText: {
          fontSize: 14,
          margin: [0, 0, 0, 15],
        },
        subheader: {
          fontSize: 12,
          bold: true,
          margin: [0, 15, 0, 5],
        },
        tableExample: {
          margin: [0, 5, 0, 5],
          widths: ['25%', '25%', '25%', '25%'],
        },
        tableExampleValidationError: {
          margin: [0, 2, 0, 20],
          widths: ['25%', '25%', '25%', '25%'],
        },
        tableHeader: {
          fontSize: 11,
          color: 'black',
        },
        tableText: {
          color: 'red',
          fontSize: 11,
        },
        tableTextCompositeError: {
          color: 'red',
          fontSize: 11,
          margin: [5, 0, 0, 0],
        },
        tableTextCompositeErrorDetails: {
          color: 'black',
          fontSize: 11,
          margin: [10, 0, 0, 0],
        },
        textEmail: {
          color: 'blue',
          decoration: 'underline',
        },
        uploadedText: {
          fontSize: 11,
        },
        bottomText: {
          fontSize: 11,
        },
      },
    };

    pdfMake.createPdf(dd).download(downloadErrorReportName);
  }

  public compositeErrorHeader(): any {
    let header = '';
    header = this.compositeErrorList.length !== 0 ? 'Mixed Collateral Errors' : '';
    return header;
  }

  public validationErrorTableData(): any {
    const columns = ['cusip', 'rowNumber', 'errorMessage', ''];
    const columnHeader = [
      {
        text: 'CUSIP',
        style: 'tableHeader',
        fillColor: '#f2f2f2',
        border: [false, true, false, false],
        margin: [0, 5, 0, 5],
      },
      {
        text: 'Row',
        style: 'tableHeader',
        fillColor: '#f2f2f2',
        border: [false, true, false, false],
        margin: [0, 5, 0, 5],
      },
      {
        text: 'Error',
        style: 'tableHeader',
        fillColor: '#f2f2f2',
        border: [false, true, false, false],
        margin: [0, 5, 0, 5],
      },
      {},
    ];

    const data = this.validationErrorList;

    const bodyValidation = [];

    bodyValidation.push(columnHeader);

    data.forEach(function (row) {
      const dataRow = [];

      columns.forEach(function (column) {
        if (column === 'errorMessage') {
          dataRow.push({
            text: row[column],
            style: 'tableText',
            border: [false, true, false, true],
            margin: [0, 5, 0, 5],
          });
        } else if (column === '') {
          dataRow.push({});
        } else {
          dataRow.push({
            text: row[column],
            style: 'tableHeader',
            border: [false, true, false, true],
            margin: [0, 5, 0, 5],
          });
        }
      });

      bodyValidation.push(dataRow);
    });

    return bodyValidation;
  }

  public compositeErrorTableData(): any {
    const columns = ['errorMessage'];
    let columnHeader = [
      {
        text: 'Description',
        style: 'tableHeader',
        colSpan: 4,
        alignment: 'left',
        border: [false, true, false, false],
        fillColor: '#f2f2f2',
        margin: [0, 5, 0, 5],
      },
      {},
      {},
      {},
    ];

    const data = this.compositeErrorList;

    const bodyCompositeError = [];

    if (data && data.length !== 0) {
      bodyCompositeError.push(columnHeader);
      data.forEach(function (row) {
        const dataRow = [];

        columns.forEach(function (column) {
          dataRow.push(
            {
              stack: [
                row[column],
                {
                  ul: this.compositeErrorRowDetails(row.errorValueDetails),
                  style: 'tableTextCompositeErrorDetails',
                },
              ],
              style: 'tableTextCompositeError',
              border: [false, true, false, true],
              margin: [0, 5, 0, 5],
            },
            {},
            {},
            {},
          );
        }, this);

        bodyCompositeError.push(dataRow);
      }, this);
    } else {
      columnHeader = [];
      bodyCompositeError.push(columnHeader);
    }

    return bodyCompositeError;
  }

  public compositeErrorRowDetails(val: any): any {
    const errorDetails = [];

    val.forEach(function (item) {
      errorDetails.push(item.value + ' (rows : ' + item.rowString + ' )');
    });

    return errorDetails;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
